import React, { useEffect, useState } from 'react';
import { StepProps, Specialty, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { Droplets, Heart, FlaskConical, Stethoscope, ArrowRight, Eye, BarChart3, AlertCircle } from 'lucide-react';
type PerceptionAnswer = 'Early / Proactive' | 'Balanced' | 'Late / Cautious';
interface SpecialtyReality {
  id: Specialty;
  icon: React.ElementType;
  stateKey: 'perceptionNeph' | 'perceptionCardio' | 'perceptionEndo' | 'perceptionPCP';
  reality: PerceptionAnswer;
  realityLabel: string;
  insight: string;
}
const realities: SpecialtyReality[] = [{
  id: 'Nephrologist',
  icon: Droplets,
  stateKey: 'perceptionNeph',
  reality: 'Late / Cautious',
  realityLabel: 'Late / Cautious',
  insight: 'Most nephrologists adopt a cautious, late-stage approach — driven by late referrals and safety concerns.'
}, {
  id: 'Cardiologist',
  icon: Heart,
  stateKey: 'perceptionCardio',
  reality: 'Early / Proactive',
  realityLabel: 'Early / Proactive',
  insight: 'Cardiologists are significantly more likely to initiate early treatment and embrace multi-modal therapies.'
}, {
  id: 'Endocrinologist',
  icon: FlaskConical,
  stateKey: 'perceptionEndo',
  reality: 'Balanced',
  realityLabel: 'Balanced',
  insight: 'Endocrinologists are proactive through a metabolic lens but often defer non-glycaemic escalation to other specialists.'
}, {
  id: 'PCP',
  icon: Stethoscope,
  stateKey: 'perceptionPCP',
  reality: 'Late / Cautious',
  realityLabel: 'Late / Cautious',
  insight: 'PCPs manage early-stage CKD conservatively and tend to refer rather than escalate treatment.'
}];
export function PerceptionReveal({
  state,
  nextStep
}: StepProps) {
  const [revealedCount, setRevealedCount] = useState(0);
  // Auto-skip if user chose direct exploration
  useEffect(() => {
    if (!state.perceptionMode) {
      nextStep();
    }
  }, [state.perceptionMode]);
  // Don't render anything if skipping
  if (!state.perceptionMode) {
    return null;
  }
  useEffect(() => {
    const interval = setInterval(() => {
      setRevealedCount(prev => {
        if (prev >= realities.length) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 800);
    return () => clearInterval(interval);
  }, []);
  const getMatchStatus = (perception: PerceptionAnswer | null, reality: PerceptionAnswer): {
    label: string;
    color: string;
    bg: string;
  } => {
    if (!perception) return {
      label: 'No answer',
      color: '#9ca3af',
      bg: '#f3f4f6'
    };
    if (perception === reality) return {
      label: 'Correct',
      color: '#16a34a',
      bg: '#f0fdf4'
    };
    return {
      label: 'Misaligned',
      color: '#dc2626',
      bg: '#fef2f2'
    };
  };
  const totalCorrect = realities.filter(r => state[r.stateKey] === r.reality).length;
  const accuracyPct = Math.round(totalCorrect / realities.length * 100);
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto" data-id="element-340">
      <div className="max-w-4xl w-full mx-auto" data-id="element-341">
        <div className="text-center mb-12" data-id="element-342">
          <div className="inline-flex items-center justify-center p-3 bg-brand/10 text-brand rounded-full mb-6" data-id="element-343">
            <Eye size={32} data-id="element-344" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-dark mb-4" data-id="element-345">
            Perception vs Reality
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-346">
            How well did your assumptions match observed specialty behavior?
          </p>
        </div>

        <div className="space-y-4 mb-10" data-id="element-347">
          {realities.map((r, i) => {
          const Icon = r.icon;
          const color = SPECIALTY_COLORS[r.id];
          const perception = state[r.stateKey] as PerceptionAnswer | null;
          const match = getMatchStatus(perception, r.reality);
          const isRevealed = i < revealedCount;
          return <motion.div key={r.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: isRevealed ? 1 : 0.3,
            y: isRevealed ? 0 : 20
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden" data-id="element-348">
                {/* Header row */}
                <div className="flex items-center p-5 border-b border-gray-100" data-id="element-349">
                  <div className="p-2 rounded-full mr-3" style={{
                backgroundColor: `${color}15`,
                color
              }} data-id="element-350">
                    <Icon size={20} data-id="element-351" />
                  </div>
                  <span className="text-lg font-bold text-dark flex-grow" data-id="element-352">
                    {r.id}s
                  </span>
                  {isRevealed && <motion.span initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full" style={{
                color: match.color,
                backgroundColor: match.bg
              }} data-id="element-353">
                      {match.label}
                    </motion.span>}
                </div>

                {isRevealed && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="p-5" data-id="element-354">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-355">
                      <div className="bg-gray-50 rounded-xl p-4" data-id="element-356">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-357">
                          Your Prediction
                        </p>
                        <p className="text-base font-semibold text-dark" data-id="element-358">
                          {perception || 'Not answered'}
                        </p>
                      </div>
                      <div className="rounded-xl p-4" style={{
                  backgroundColor: `${color}10`
                }} data-id="element-359">
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1" data-id="element-360">
                          Observed Reality
                        </p>
                        <p className="text-base font-semibold" style={{
                    color
                  }} data-id="element-361">
                          {r.realityLabel}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed italic" data-id="element-362">
                      {r.insight}
                    </p>
                  </motion.div>}
              </motion.div>;
        })}
        </div>

        {revealedCount >= realities.length && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-363">
            <div className="bg-dark text-white rounded-3xl p-8 text-center mb-10" data-id="element-364">
              <h3 className="text-4xl font-black mb-2" data-id="element-365">{accuracyPct}%</h3>
              <p className="text-lg text-gray-300 mb-4" data-id="element-366">Perception Accuracy</p>
              <p className="text-base text-gray-400 max-w-lg mx-auto" data-id="element-367">
                {accuracyPct >= 75 ? 'Strong alignment — your understanding of specialty behavior is well-calibrated.' : accuracyPct >= 50 ? 'Partial alignment — some assumptions matched reality, but key gaps exist.' : 'Significant gap between perception and reality — this misalignment may impact strategy execution.'}
              </p>
            </div>

            <div className="flex justify-center pb-8" data-id="element-368">
              <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-brand rounded-full hover:bg-red-700 transition-colors shadow-lg" data-id="element-369">
                See Your Personal Gap Analysis
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-370" />
              </button>
            </div>
          </motion.div>}
      </div>
    </div>;
}